/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 캘린더 컴포넌트
 * 버전 : 1.0
 * 파일명 : RecruitCalendar.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================
 * 수정 : 2026-01-09, 강동형 : 플로팅 버튼 추가
 * 수정 : 2026-01-13, 강동형 : 축제 등록 다이얼로그 UI 추가
 * 수정 : 2026-01-19, 강동형 : 축제 값 임시 파싱
 * 수정 : 2026-02-04, 강동형 : 축제 데이터 연결
 * 수정 : 2026-02-05, 강동형 : 축제 추가 버튼 Zindex 조정
 *================================================================= */

import { Calendar, dateFnsLocalizer} from 'react-big-calendar'
import {format, parse, startOfWeek, getDay, isWithinInterval, startOfDay, endOfDay} from 'date-fns'
import { ko } from 'date-fns/locale';
import './RecruitCalendar.scss'
import React, { useEffect, useState } from 'react';
import { useRecruitStore } from 'src/stores/RecruitStore';
import { ArmyRecruitDataType, LoadArmyRecruit, LoadFestival } from './LoadArmyRecruit';
import styled from 'styled-components';
import { AppImage } from 'src/assets/images/AppImage';

const locales = {
    'ko': ko,
  };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

const Wrapper = styled.div`
  position: relative;
`
const ButtonWrapper = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  right: 25px;
  bottom: 20px;
  padding: 16px;
  
  border-radius: 100%;
  border : none;
  background-color: #0B4D3C;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  z-index: 900;
`

const RCalendar = Calendar as unknown as React.FC<any>;

type CalendarEvent = {
  title: string;
  start: Date;
  end: Date;
  announce_id: number;
  course_name: string;
  course_type?: string
  course_status : string
  recruitment_type: string
  wide_loc?:string
  city_loc?:string
  address? : string
  latitude? : string,
  longitude? : string,
  description? : string,
};

type RecruitCalendarProps = {
  whichTab:'모집 일정' | '축제 정보'
}

const RecruitCalendar = ({whichTab}:RecruitCalendarProps) => {
  const setField = useRecruitStore((state)=>state.setField)
  const year = useRecruitStore((state)=>state.year)
  const month = useRecruitStore((state)=>state.month)
  const type = useRecruitStore((state)=>state.type)
  const status = useRecruitStore((state)=>state.status)
  const ArmyRecruitData = useRecruitStore((state)=>state.ArmyRecruitData)
  const [events,setEvents] = useState<CalendarEvent[]>([])

  const convertToEvents = (data: ArmyRecruitDataType[]): CalendarEvent[] => {
    if(whichTab === '모집 일정'){
      return data.map(item => {
      // 먼저 item.etc를 사용해 기존의 이벤트를 변환
      const events = item.schedules?.map(schedule => {
        return {
          title: item.title,
          start: new Date(schedule.start_date),
          end: new Date(schedule.end_date),
          announce_id: item.announce_id,
          course_type: schedule.course_name === '입영일' || schedule.course_name === '임관일' || schedule.course_name === '접수일정' ? schedule.course_name : '추가과정',
          course_name: schedule.course_name,
          course_status:item.status,
          quilify : item.requirements,
          urlLink : item.source_url,
          sessionData: item.schedules,
          recruitment_type : item.recruitment_type
        };
      });
      return [...events];
    }).flat();
    }
    else{
      const events = (data ?? []).map((item: any) => ({
        title: item.event_name,
        start: new Date(item.start_date),
        end: new Date(item.end_date),
        announce_id: item.event_id,
        course_name: item.event_name,
        course_status:item.course_status,
        wide_loc: item.wide_loc,
        city_loc: item.city_loc,
        address: item.address,
        latitude: item.latitude,
        longitude: item.longitude,
        description: item.description,
        urlLink: item.url,
        recruitment_type: item.event_type === "축제" ? "축제" : "박람회",
      }));
      return events
    }
  };

  useEffect(() => {
      const fetchData = async () => {
        if(whichTab==='모집 일정'){
          const response = await LoadArmyRecruit({year:Number(year),month:Number(month)});
          setEvents(convertToEvents(response.data))
        }
        else{
          const response = await LoadFestival({year:Number(year),month:Number(month)});
          setEvents(convertToEvents(response.data))
        }
      }
      fetchData()
  }, [year,month,whichTab]);

  useEffect(()=>{
    if(whichTab==='모집 일정') return
    if(ArmyRecruitData.length===0) return
    setEvents(convertToEvents(ArmyRecruitData))
  },[ArmyRecruitData])


  const filteredEvents = events.filter((event) => {
    const typeMatch = type === '모집유형' || type === '축제유형' || event.recruitment_type === type;
    const statusMatch = status === '상태' || event.course_status.replace(/\s/g, '') === status.replace(/\s/g, '');
    return typeMatch && statusMatch;
  });

  const onDrillDown = (date: Date, view: string, event: React.SyntheticEvent)=>{
    // 여기서 drilldown 기본 동작을 막는다
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const eventArray = filteredEvents.filter(event => {
      return isWithinInterval(date, {
        start: startOfDay(new Date(event.start)),
        end: endOfDay(new Date(event.end)),
      });
    });
    setField('dialogData',eventArray)
    setField('dialogCurrentDate',date)
    setField('isOpen',true)
  }
  const currentDate = new Date(Number(year), Number(month) - 1, 1); 

  const nowBgColorMap: Record<string, string> = {
    '접수일정': '#0B4D3C',
    '추가과정': '#117E61',
    '입영일': '#49CDAA',
    '임관일': '#A4E6D5',
  };

  const eventBgColorMap: Record<string, string> = {
    '축제': '#3D598A',
    '박람회': '#B17AD1',
  };

  const nowColorMap: Record<string, string> = {
    '접수일정': 'white',
    '추가과정': 'white',
    '입영일': '#0B4D3C',
    '임관일': '#0B4D3C',
  };

  const eventColorMap: Record<string, string> = {
    '축제': 'white',
    '박람회': 'white',
  };

  const handleSelectEvent = (events:CalendarEvent)=>{
    if(whichTab==='모집 일정'){
      setField('selected',events)
      setField('isOpen',true)
    }
  }

  const onClickRegisterEvent = ()=>{
    setField('dialogState','축제 등록')
    setField('isOpen',true)
  }

  return(
    <Wrapper>
      <RCalendar
      localizer={localizer}
      events={filteredEvents}
      startAccessor="start"
      endAccessor="end"
      style={{ height: '600px' }}
      date={currentDate}
      toolbar = {false} // 상단 날짜선택 옵션 툴바
      drilldownView="day" // 날짜 클릭시 실행하는 이벤트트
      onDrillDown={onDrillDown}
      onSelectEvent={handleSelectEvent}
      eventPropGetter={(event : any) => {
        const backgroundColor = whichTab==='모집 일정' ? nowBgColorMap[event.course_type] : eventBgColorMap[event.recruitment_type] || 'lightgray';
        const color = whichTab==='모집 일정' ? nowColorMap[event.course_type] : eventColorMap[event.recruitment_type] || 'black'

        return {
          style: {
            backgroundColor: backgroundColor,
            color: color,
            borderRadius: '5px',
            border: 'none',
            padding: '3px',
            fontSize:'14px',
            textAlign:'center'
          },
        };
      }}
      />
      {whichTab === '축제 정보' &&
        <ButtonWrapper onClick={onClickRegisterEvent}>
          <AppImage name='IconPlus'/>
        </ButtonWrapper>
      }
    </Wrapper>
  )
}

export default RecruitCalendar